// hooks/useStaleSuggestionsPrompt.ts
/**
 * Detects when optimizer state transitions from 'complete' → 'stale'
 * (i.e., the user made a plan change that invalidated existing suggestions)
 * and controls whether to show the StaleSuggestionsModal.
 *
 * The modal is shown at most once per stale transition. After the user
 * dismisses it (with any choice), it won't reappear until the optimizer
 * produces fresh suggestions and they go stale again.
 */
import { useRef, useCallback, useState } from 'react';
import type { OptimizationStatus } from '../types';

interface UseStaleSuggestionsPromptReturn {
  /** Whether the stale-suggestions modal should be shown */
  showStaleSuggestionsModal: boolean;
  /** Call when optimizer status updates — detects complete→stale transition */
  onOptimizationStatusChange: (status: OptimizationStatus | null | undefined) => void;
  /** Dismiss the modal (any action: recalculate, clear, or keep) */
  dismissStaleSuggestionsModal: () => void;
}

export const useStaleSuggestionsPrompt = (
  hasSuggestions: boolean,
): UseStaleSuggestionsPromptReturn => {
  const [showModal, setShowModal] = useState(false);
  const prevStateRef = useRef<string | null>(null);
  // Once dismissed for a stale transition, don't show again until next fresh cycle
  const dismissedRef = useRef(false);

  const onOptimizationStatusChange = useCallback((status: OptimizationStatus | null | undefined) => {
    const newState = status?.state ?? null;
    const prevState = prevStateRef.current;
    prevStateRef.current = newState;

    // Detect complete → stale transition
    if (prevState === 'complete' && newState === 'stale' && hasSuggestions && !dismissedRef.current) {
      setShowModal(true);
    }

    // Reset dismissed flag when optimizer produces fresh results
    if (newState === 'complete') {
      dismissedRef.current = false;
    }
  }, [hasSuggestions]);

  const dismissStaleSuggestionsModal = useCallback(() => {
    setShowModal(false);
    dismissedRef.current = true;
  }, []);

  return {
    showStaleSuggestionsModal: showModal,
    onOptimizationStatusChange,
    dismissStaleSuggestionsModal,
  };
};
